import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

interface RightSidePanelState {
	isOpen: boolean;
	width: string | number;
}

export interface RightSidePanelDestination {
	key: string;
	open: () => void;
}

interface RightSidePanelNavigationState {
	current?: RightSidePanelDestination;
	back: RightSidePanelDestination[];
	forward: RightSidePanelDestination[];
}

interface RightSidePanelActionsContextValue {
	registerPanel: (width: string | number) => void;
	unregisterPanel: () => void;
	startNavigation: (destination: RightSidePanelDestination) => void;
	followNavigation: (destination: RightSidePanelDestination) => void;
	replaceNavigation: (destination: RightSidePanelDestination) => void;
	adoptNavigation: (destination: RightSidePanelDestination) => void;
	clearNavigation: () => void;
	goBack: () => void;
	goForward: () => void;
}

interface RightSidePanelStateContextValue {
	panelState: RightSidePanelState;
	currentDestinationKey?: string;
	canGoBack: boolean;
	canGoForward: boolean;
}

interface RightSidePanelContextValue extends RightSidePanelActionsContextValue, RightSidePanelStateContextValue {}

const DEFAULT_WIDTH = '440px';

const RightSidePanelActionsContext = createContext<RightSidePanelActionsContextValue>({
	registerPanel: () => {},
	unregisterPanel: () => {},
	startNavigation: () => {},
	followNavigation: () => {},
	replaceNavigation: () => {},
	adoptNavigation: () => {},
	clearNavigation: () => {},
	goBack: () => {},
	goForward: () => {},
});

const RightSidePanelStateContext = createContext<RightSidePanelStateContextValue>({
	panelState: { isOpen: false, width: DEFAULT_WIDTH },
	canGoBack: false,
	canGoForward: false,
});

/**
 * Tracks whether any RightSidePanel is currently open and its width.
 * Kept as shared panel state for consumers that need to respond to drawer placement.
 */
export const RightSidePanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [panelState, setPanelState] = useState<RightSidePanelState>({ isOpen: false, width: DEFAULT_WIDTH });
	const [navigationState, setNavigationState] = useState<RightSidePanelNavigationState>({ back: [], forward: [] });
	const navigationStateRef = React.useRef(navigationState);
	useLayoutEffect(() => {
		navigationStateRef.current = navigationState;
	}, [navigationState]);

	const registerPanel = useCallback((width: string | number) => {
		setPanelState({ isOpen: true, width });
	}, []);

	const unregisterPanel = useCallback(() => {
		setPanelState({ isOpen: false, width: DEFAULT_WIDTH });
	}, []);

	const startNavigation = useCallback((destination: RightSidePanelDestination) => {
		setNavigationState({ current: destination, back: [], forward: [] });
	}, []);

	const followNavigation = useCallback((destination: RightSidePanelDestination) => {
		setNavigationState((current) => {
			if (current.current?.key === destination.key) return current;
			return {
				current: destination,
				back: current.current ? [...current.back, current.current] : [],
				forward: [],
			};
		});
	}, []);

	// Moving through the page's current list changes which item occupies this point in the trail.
	// Back/Forward therefore retain their meaning instead of duplicating Previous/Next item.
	const replaceNavigation = useCallback((destination: RightSidePanelDestination) => {
		setNavigationState((current) => ({ ...current, current: destination }));
	}, []);

	// A drawer opened directly from a URL has no initiating click to register it. Adopt it as a new
	// trail only when a cross-feature follow has not already installed the same destination.
	const adoptNavigation = useCallback((destination: RightSidePanelDestination) => {
		setNavigationState((current) =>
			current.current?.key === destination.key ? current : { current: destination, back: [], forward: [] },
		);
	}, []);

	const clearNavigation = useCallback(() => {
		setNavigationState({ back: [], forward: [] });
	}, []);

	const goBack = useCallback(() => {
		const current = navigationStateRef.current;
		const destination = current.back.at(-1);
		if (!destination || !current.current) return;

		setNavigationState({
			current: destination,
			back: current.back.slice(0, -1),
			forward: [current.current, ...current.forward],
		});
		destination.open();
	}, []);

	const goForward = useCallback(() => {
		const current = navigationStateRef.current;
		const destination = current.forward[0];
		if (!destination || !current.current) return;

		setNavigationState({
			current: destination,
			back: [...current.back, current.current],
			forward: current.forward.slice(1),
		});
		destination.open();
	}, []);

	// Stable value so consumers only rerender when panelState actually changes, not
	// on every unrelated rerender of an ancestor provider. registerPanel/unregisterPanel
	// are already useCallback-stable, so panelState is the only real dependency.
	const actionsValue = useMemo(
		() => ({
			registerPanel,
			unregisterPanel,
			startNavigation,
			followNavigation,
			replaceNavigation,
			adoptNavigation,
			clearNavigation,
			goBack,
			goForward,
		}),
		[
			registerPanel,
			unregisterPanel,
			startNavigation,
			followNavigation,
			replaceNavigation,
			adoptNavigation,
			clearNavigation,
			goBack,
			goForward,
		],
	);
	const stateValue = useMemo(
		() => ({
			panelState,
			currentDestinationKey: navigationState.current?.key,
			canGoBack: navigationState.back.length > 0,
			canGoForward: navigationState.forward.length > 0,
		}),
		[panelState, navigationState],
	);

	return (
		<RightSidePanelActionsContext.Provider value={actionsValue}>
			<RightSidePanelStateContext.Provider value={stateValue}>{children}</RightSidePanelStateContext.Provider>
		</RightSidePanelActionsContext.Provider>
	);
};

// The provider and its hook deliberately share the private context.
// eslint-disable-next-line react-refresh/only-export-components
export const useRightSidePanel = (): RightSidePanelContextValue => ({
	...useContext(RightSidePanelActionsContext),
	...useContext(RightSidePanelStateContext),
});

// eslint-disable-next-line react-refresh/only-export-components
export const useRightSidePanelActions = () => useContext(RightSidePanelActionsContext);
